import type { QuickFilter } from "@/components/shared/DataTable-modular/types";
import {
  COMPANY_STATUS_OPTIONS,
  COMPANY_QUICK_FILTERS,
} from "@/features/companies/components/EnhancedCompaniesConfig";
import {
  TIME_ENTRY_STATUS_OPTIONS,
  TIME_ENTRY_TYPE_OPTIONS,
  SYNC_STATUS_OPTIONS,
  timeEntryTemplateTagSets,
} from "@/features/time-entries/config/constants";
import {
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
  TICKET_CATEGORY_OPTIONS,
} from "@/features/tickets/config/options";
import { ticketTemplateTagSets } from "@/features/tickets/config/templates";
import { taskTemplateTagSets } from "@/features/tasks/components/TaskConfig";

type EmptyEntityConstants = Record<string, never>;

export interface CompanyEntityConstants {
  COMPANY_STATUS_OPTIONS: typeof COMPANY_STATUS_OPTIONS;
  COMPANY_QUICK_FILTERS: typeof COMPANY_QUICK_FILTERS;
}

export interface TimeEntryEntityConstants {
  TIME_ENTRY_STATUS_OPTIONS: typeof TIME_ENTRY_STATUS_OPTIONS;
  TIME_ENTRY_TYPE_OPTIONS: typeof TIME_ENTRY_TYPE_OPTIONS;
  SYNC_STATUS_OPTIONS: typeof SYNC_STATUS_OPTIONS;
  timeEntryTemplateTagSets: typeof timeEntryTemplateTagSets;
}

export interface TicketEntityConstants {
  TICKET_PRIORITY_OPTIONS: typeof TICKET_PRIORITY_OPTIONS;
  TICKET_STATUS_OPTIONS: typeof TICKET_STATUS_OPTIONS;
  TICKET_CATEGORY_OPTIONS: typeof TICKET_CATEGORY_OPTIONS;
  ticketTemplateTagSets: typeof ticketTemplateTagSets;
}

export interface TaskEntityConstants {
  taskTemplateTagSets: typeof taskTemplateTagSets;
}

/**
 * Shared UI constants keyed by entity registry id.
 * Import via `getEntityConstants("companies")` — not from feature *Config files.
 */
export type EntityConstantsMap = {
  contacts: EmptyEntityConstants;
  companies: CompanyEntityConstants;
  deals: EmptyEntityConstants;
  tickets: TicketEntityConstants;
  time_entries: TimeEntryEntityConstants;
  tasks: TaskEntityConstants;
  communications: EmptyEntityConstants;
  recurring_transactions: EmptyEntityConstants;
  recurring_charges: EmptyEntityConstants;
  social_media_posts: EmptyEntityConstants;
  email_campaigns: EmptyEntityConstants;
  employees: EmptyEntityConstants;
  offerings: EmptyEntityConstants;
};

export const ENTITY_CONSTANTS: EntityConstantsMap = {
  contacts: {},
  companies: {
    COMPANY_STATUS_OPTIONS,
    COMPANY_QUICK_FILTERS,
  },
  deals: {},
  tickets: {
    TICKET_PRIORITY_OPTIONS,
    TICKET_STATUS_OPTIONS,
    TICKET_CATEGORY_OPTIONS,
    ticketTemplateTagSets,
  },
  time_entries: {
    TIME_ENTRY_STATUS_OPTIONS,
    TIME_ENTRY_TYPE_OPTIONS,
    SYNC_STATUS_OPTIONS,
    timeEntryTemplateTagSets,
  },
  tasks: {
    taskTemplateTagSets,
  },
  communications: {},
  recurring_transactions: {},
  recurring_charges: {},
  social_media_posts: {},
  email_campaigns: {},
  employees: {},
  offerings: {},
};

/** Entity keys that expose shared constants through the registry. */
export const ENTITIES_WITH_CONSTANTS = [
  "companies",
  "tickets",
  "time_entries",
  "tasks",
] as const satisfies readonly (keyof EntityConstantsMap)[];

export type EntityConstantsKey = (typeof ENTITIES_WITH_CONSTANTS)[number];

export function hasEntityConstants(
  entity: keyof EntityConstantsMap
): entity is EntityConstantsKey {
  return ENTITIES_WITH_CONSTANTS.includes(entity as EntityConstantsKey);
}

/** Shared field options, quick filters, and template tag sets for an entity. */
export function getEntityConstants<K extends keyof EntityConstantsMap>(
  entity: K
): EntityConstantsMap[K] {
  return ENTITY_CONSTANTS[entity];
}
